import React from 'react';
import { Order } from '../../lib/types';
import { formatCurrency, formatDate } from '../../lib/utils';
import { ReportType } from './ReportsFilterBar';

interface Props {
  reportType: ReportType;
  filteredOrders: Order[];
  totalSpend: number;
  currency: string;
  locale: string;
  dateFrom: string;
  dateTo: string;
}

const spendStyle: React.CSSProperties = { color: 'var(--teal)' };

function groupReduce(orders: Order[], keyFn: (o: Order) => string) {
  const grouped: Record<string, { orders: Order[]; count: number; spend: number }> = {};
  orders.forEach(o => {
    const k = keyFn(o);
    if (!grouped[k]) grouped[k] = { orders: [], count: 0, spend: 0 };
    grouped[k].orders.push(o);
    grouped[k].count++;
    grouped[k].spend += o.total_cost || 0;
  });
  return grouped;
}

export function ReportsOutput({ reportType, filteredOrders, totalSpend, currency, locale, dateFrom, dateTo }: Props) {
  if (reportType === 'all') {
    return (
      <>
        <h3>All Orders Report</h3>
        <div className="rep-sub">
          {filteredOrders.length} orders · {formatCurrency(totalSpend, currency)} total spend
          {dateFrom && ` · From ${dateFrom}`}{dateTo && ` · To ${dateTo}`}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th><th>Staff</th><th>Dept</th><th>Vendor</th>
              <th>Ref #</th><th>Category</th><th>Status</th><th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map(o => (
              <tr key={o.id}>
                <td className="mono">{formatDate(o.order_date, locale)}</td>
                <td>{o.staff_name || '—'}</td>
                <td>{o.department_name || '—'}</td>
                <td>{o.vendor || '—'}</td>
                <td className="mono">{o.order_number || '—'}</td>
                <td>{o.category || '—'}</td>
                <td>{o.status}</td>
                <td className="mono">{o.total_cost ? formatCurrency(o.total_cost, currency) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (reportType === 'staff') {
    const grouped = groupReduce(filteredOrders, o => o.staff_name || 'Unassigned');
    const sorted = Object.entries(grouped).sort((a, b) => b[1].spend - a[1].spend);
    return (
      <>
        <h3>Orders by Staff Member</h3>
        <div className="rep-sub">{sorted.length} staff · {formatCurrency(totalSpend, currency)} total</div>
        <table>
          <thead><tr><th>Staff</th><th>Orders</th><th>Total Spend</th><th>Avg per Order</th></tr></thead>
          <tbody>
            {sorted.map(([name, data]) => (
              <tr key={name}>
                <td><strong>{name}</strong></td>
                <td className="mono">{data.count}</td>
                <td className="mono" style={spendStyle}>{formatCurrency(data.spend, currency)}</td>
                <td className="mono">{formatCurrency(data.spend / data.count, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (reportType === 'vendor') {
    const grouped = groupReduce(filteredOrders, o => o.vendor || 'Unknown');
    const sorted = Object.entries(grouped).sort((a, b) => b[1].spend - a[1].spend);
    return (
      <>
        <h3>Spend by Vendor</h3>
        <div className="rep-sub">{sorted.length} vendors · {formatCurrency(totalSpend, currency)} total</div>
        <table>
          <thead><tr><th>Vendor</th><th>Orders</th><th>Total Spend</th><th>% of Total</th></tr></thead>
          <tbody>
            {sorted.map(([vendor, data]) => (
              <tr key={vendor}>
                <td><strong>{vendor}</strong></td>
                <td className="mono">{data.count}</td>
                <td className="mono" style={spendStyle}>{formatCurrency(data.spend, currency)}</td>
                <td className="mono">{totalSpend > 0 ? Math.round((data.spend / totalSpend) * 100) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (reportType === 'department') {
    const grouped = groupReduce(filteredOrders, o => o.department_name || 'Unassigned');
    const sorted = Object.entries(grouped).sort((a, b) => b[1].spend - a[1].spend);
    return (
      <>
        <h3>Spend by Department</h3>
        <div className="rep-sub">{sorted.length} departments · {formatCurrency(totalSpend, currency)} total</div>
        <table>
          <thead><tr><th>Department</th><th>Orders</th><th>Total Spend</th><th>% of Total</th></tr></thead>
          <tbody>
            {sorted.map(([dept, data]) => (
              <tr key={dept}>
                <td><strong>{dept}</strong></td>
                <td className="mono">{data.count}</td>
                <td className="mono" style={spendStyle}>{formatCurrency(data.spend, currency)}</td>
                <td className="mono">{totalSpend > 0 ? Math.round((data.spend / totalSpend) * 100) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (reportType === 'monthly') {
    const grouped: Record<string, { count: number; spend: number }> = {};
    filteredOrders.forEach(o => {
      if (!o.order_date) return;
      const d = new Date(o.order_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[key]) grouped[key] = { count: 0, spend: 0 };
      grouped[key].count++;
      grouped[key].spend += o.total_cost || 0;
    });
    const sorted = Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
    return (
      <>
        <h3>Monthly Spend Summary</h3>
        <div className="rep-sub">{sorted.length} months · {formatCurrency(totalSpend, currency)} total</div>
        <table>
          <thead><tr><th>Month</th><th>Orders</th><th>Total Spend</th><th>Avg per Order</th></tr></thead>
          <tbody>
            {sorted.map(([month, data]) => {
              const d = new Date(month + '-01');
              const label = d.toLocaleString(locale, { month: 'short', year: 'numeric' });
              return (
                <tr key={month}>
                  <td><strong>{label}</strong></td>
                  <td className="mono">{data.count}</td>
                  <td className="mono" style={spendStyle}>{formatCurrency(data.spend, currency)}</td>
                  <td className="mono">{formatCurrency(data.spend / data.count, currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }

  if (reportType === 'status') {
    const grouped = groupReduce(filteredOrders, o => o.status || 'Unknown');
    const statusOrder = ['Pending', 'Ordered', 'Delivered', 'Closed'];
    const sorted = Object.entries(grouped).sort((a, b) => statusOrder.indexOf(a[0]) - statusOrder.indexOf(b[0]));
    return (
      <>
        <h3>Orders by Status</h3>
        <div className="rep-sub">{filteredOrders.length} orders · {formatCurrency(totalSpend, currency)} total</div>
        <table>
          <thead><tr><th>Status</th><th>Orders</th><th>Total Spend</th><th>% of Orders</th></tr></thead>
          <tbody>
            {sorted.map(([status, data]) => (
              <tr key={status}>
                <td><strong>{status}</strong></td>
                <td className="mono">{data.count}</td>
                <td className="mono" style={spendStyle}>{formatCurrency(data.spend, currency)}</td>
                <td className="mono">{filteredOrders.length > 0 ? Math.round((data.count / filteredOrders.length) * 100) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  // category
  const grouped = groupReduce(filteredOrders, o => o.category || 'Uncategorised');
  const sorted = Object.entries(grouped).sort((a, b) => b[1].spend - a[1].spend);
  return (
    <>
      <h3>Orders by Category</h3>
      <div className="rep-sub">{sorted.length} categories · {formatCurrency(totalSpend, currency)} total</div>
      <table>
        <thead><tr><th>Category</th><th>Orders</th><th>Total Spend</th><th>% of Total</th></tr></thead>
        <tbody>
          {sorted.map(([cat, data]) => (
            <tr key={cat}>
              <td><strong>{cat}</strong></td>
              <td className="mono">{data.count}</td>
              <td className="mono" style={spendStyle}>{formatCurrency(data.spend, currency)}</td>
              <td className="mono">{totalSpend > 0 ? Math.round((data.spend / totalSpend) * 100) : 0}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
